import { makeOrientationAck } from './orientation-ack'
import type { Tutorial } from '@/shared/components/tutorial/types'

/**
 * Context the receptionist tutorial reads to evaluate completion.
 *
 * Every step is a **localStorage orientation ack** — the receptionist marks a
 * step done by clicking its deep-link CTA (which navigates to the screen). No
 * club-wide proxy signals: a proxy like "club has bookings/clients" pre-ticks
 * the step on a populated club (a brand-new receptionist saw "Clientes" already
 * checked — wrong) AND clicking the CTA didn't complete it. Staff tier can't
 * write the club doc, so club-side stamps aren't an option — localStorage acks
 * are scoped to the signed-in operator and club, including shared tablets.
 * Private-mode / storage-disabled reads as
 * not-seen: steps stay visible but never error.
 */
export interface ReceptionSetupCtx {
  hoySeen: boolean
  cobrarSeen: boolean
  clientesSeen: boolean
  ventasSeen: boolean
}

const hoyAck = makeOrientationAck('reception.hoy')
const cobrarAck = makeOrientationAck('reception.cobrar')
const clientesAck = makeOrientationAck('reception.clientes')
const ventasAck = makeOrientationAck('reception.ventas')

export const isReceptionHoySeen = hoyAck.is
export const markReceptionHoySeen = hoyAck.mark
export const isReceptionCobrarSeen = cobrarAck.is
export const markReceptionCobrarSeen = cobrarAck.mark
export const isReceptionClientesSeen = clientesAck.is
export const markReceptionClientesSeen = clientesAck.mark
export const isReceptionVentasSeen = ventasAck.is
export const markReceptionVentasSeen = ventasAck.mark

/**
 * Staff-tier ("Recepcionista" by default; any custom role with
 * `tier === 'staff'`) first-turn tour: the four surfaces the desk lives on —
 * Hoy (take the day in) → Cobrar (settle a booking) → Clientes (phone lookup)
 * → Ventas (ring up an extra). Every step completes the same way: the
 * receptionist clicks its deep-link CTA and the step is acked (see the ctx
 * note). The old "Instala la app" step was dropped — its only path is Ajustes →
 * Instalación, which receptionists can't open (Ajustes is manager+); Ventas is
 * a reachable, core desk task in its place (Marce 2026-06-14).
 */
export const RECEPTION_SETUP_TUTORIAL: Tutorial<ReceptionSetupCtx> = {
  id: 'reception-setup',
  title: 'Tu primer turno',
  intro: 'Lo básico para arrancar el turno: Hoy, cobrar, Clientes y Ventas.',
  steps: [
    {
      id: 'hoy',
      label: 'Hoy',
      hint: 'Las cifras del día, el mapa de pistas y lo pendiente, a la vista. Toca un hueco libre para reservar.',
      isComplete: ({ hoySeen }) => hoySeen,
      deepLink: { label: 'Ir a Hoy', to: '/' },
      onActivate: markReceptionHoySeen,
    },
    {
      id: 'cobrar',
      label: 'Conoce el cobro de reservas',
      hint: 'En Reservas puedes abrir una reserva y consultar Cobrar: efectivo, tarjeta, transferencia o mixto, total o parcial.',
      isComplete: ({ cobrarSeen }) => cobrarSeen,
      deepLink: { label: 'Ir a Reservas', to: '/bookings' },
      onActivate: markReceptionCobrarSeen,
    },
    {
      id: 'clientes',
      label: 'Clientes',
      hint: 'Busca por teléfono, nombre o email. Las reservas pasadas se enlazan a la ficha automáticamente.',
      isComplete: ({ clientesSeen }) => clientesSeen,
      deepLink: { label: 'Ir a Clientes', to: '/members' },
      onActivate: markReceptionClientesSeen,
    },
    {
      id: 'ventas',
      label: 'Ventas',
      hint: 'Vende un extra del mostrador —agua, overgrip, una bebida—: pulsa el producto y cóbralo al momento.',
      isComplete: ({ ventasSeen }) => ventasSeen,
      deepLink: { label: 'Ir a Ventas', to: '/ventas' },
      onActivate: markReceptionVentasSeen,
    },
  ],
}
